import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

const LS_KEY = "i18nextLng";

const normalize = (v?: string | null): "tr" | "en" | null => {
  if (!v) return null;
  const s = v.toLowerCase();
  if (s.startsWith("tr")) return "tr";
  if (s.startsWith("en")) return "en";
  return null;
};

/**
 * Dil tercihini localStorage'da (i18next detector) tutar ve oturum açık
 * kullanıcılarda profiles.preferred_language ile senkronize eder:
 *  - Girişte hesaptaki tercih uygulanır (yoksa yerel tercih hesaba yazılır).
 *  - Kullanıcı dili değiştirdiğinde hem localStorage hem hesap güncellenir.
 */
export const useLanguagePreference = () => {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const hydratedFor = useRef<string | null>(null);

  // 1) Hesaptaki tercihi yükle / ilk kez yaz
  useEffect(() => {
    if (!user) {
      hydratedFor.current = null;
      return;
    }
    if (hydratedFor.current === user.id) return;
    hydratedFor.current = user.id;

    let cancelled = false;
    (async () => {
      const { data, error } = await (supabase as any)
        .from("profiles")
        .select("preferred_language")
        .eq("user_id", user.id)
        .maybeSingle();
      if (cancelled || error) return;

      const remote = normalize(data?.preferred_language);
      const local = normalize(localStorage.getItem(LS_KEY)) ?? normalize(i18n.language);

      if (remote) {
        if (remote !== normalize(i18n.language)) i18n.changeLanguage(remote);
      } else if (local) {
        await (supabase as any)
          .from("profiles")
          .update({ preferred_language: local })
          .eq("user_id", user.id);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, i18n]);

  // 2) Dil değişimlerini kalıcı yap
  useEffect(() => {
    const onChange = (lng: string) => {
      const code = normalize(lng);
      if (!code) return;
      try {
        localStorage.setItem(LS_KEY, code);
      } catch {
        /* storage unavailable */
      }
      if (!user) return;
      void (supabase as any)
        .from("profiles")
        .update({ preferred_language: code })
        .eq("user_id", user.id);
    };
    i18n.on("languageChanged", onChange);
    return () => {
      i18n.off("languageChanged", onChange);
    };
  }, [i18n, user]);
};

export default useLanguagePreference;
